function isPrime(n: number): boolean {
  if (n < 2) return false;
  if (n === 2) return true;
  if (n % 2 === 0) return false;
  for (let i = 3; i * i <= n; i += 2) {
    if (n % i === 0) return false;
  }
  return true;
}

type PredicateFn = (x: number) => boolean;

const PREDICATES: Record<string, PredicateFn> = {
  even: (x) => x % 2 === 0,
  odd: (x) => x % 2 !== 0,
  prime: isPrime,
  multipleOf3: (x) => x % 3 === 0,
  multipleOf5: (x) => x % 5 === 0,
  gt10: (x) => x > 10,
  lt5: (x) => x < 5,
  positive: (x) => x > 0,
  negative: (x) => x < 0,
  perfect: (x) => {
    if (x < 2) return false;
    let sum = 1;
    for (let i = 2; i * i <= x; i++) {
      if (x % i === 0) {
        sum += i;
        if (i !== x / i) sum += x / i;
      }
    }
    return sum === x;
  },
};

/** Return a named predicate function by id. Throws if unrecognised. */
export function namedPredicate(id: string): PredicateFn {
  const fn = PREDICATES[id];
  if (!fn) {
    throw new Error(`Unknown predicate: "${id}"`);
  }
  return fn;
}

/**
 * Parse set-builder notation `{ x : x <condition> }` and filter the universe.
 * Returns `{ members }` on success or `{ error }` on unrecognised input.
 */
export function parseSetBuilder(
  text: string,
  universe: number[],
): { members: number[] } | { error: string } {
  // Match `{ x : x <phrase> }` — allow optional whitespace variations
  const match = /^\{\s*\w+\s*:\s*\w+\s+(\S+)\s*\}$/.exec(text.trim());
  if (!match) {
    return {
      error: `"${text}" is not valid set-builder notation. Expected: { x : x <condition> }`,
    };
  }
  const phrase = match[1] ?? '';
  const fn = PREDICATES[phrase];
  if (!fn) {
    return {
      error: `Unrecognised condition "${phrase}". Known conditions: ${Object.keys(PREDICATES).join(', ')}`,
    };
  }
  return { members: universe.filter(fn) };
}
